/* [DataContext] 전역 데이터 구독 계층

   🔒 [2단계 패치] 역할별 데이터 격리
   이전에는 로그인한 사람의 역할과 무관하게 users / enrollments / english_stats
   컬렉션 '전체'를 실시간 구독했습니다. 그래서 학생 한 명이 로그인하면
     - 전 원생의 이름·전화번호·학교
     - 강사/조교의 은행명·계좌번호
     - 전 학생의 영어 어휘 점수
   가 그대로 그 학생 브라우저로 내려갔고, 원생 수에 비례해 Firestore 읽기 요금도 늘었습니다.

   이제는 이렇게 나눕니다.
     교직원      : 업무상 전체가 필요하지만, 퇴원생과 종료된 수강은 평소에 받지 않는다(아래)
     학생/학부모 : 본인 + 자녀 문서 + 교직원 명부(이름/역할/과목만) + 본인 수강 이력 + 그 수강의 반

   화면 코드는 그대로 둡니다. users 배열의 '모양'은 같고 담기는 범위만 좁아지므로
   users.find(u => u.id === lecturerId)?.name 같은 기존 코드가 그대로 동작합니다.

   [읽기량 정리 — 11-03 원장 결정]
   - english_stats 는 여기서 받지 않는다. 쓰는 화면이 영단어 관리(VocaManager) 하나뿐인데,
     출결만 보는 조교도 접속하자마자 전교생 것을 받고 학생이 시험을 볼 때마다 다시 받았다.
   - 퇴원한 학생(status 'dropped')과 종료된 수강(status 'dropped')은 평소에 받지 않는다.
     해마다 쌓이기만 하고 대부분의 화면은 쓰지 않는다. 필요한 화면은 showDropped() 를 부른다.
     교직원·학부모 계정은 상태와 상관없이 늘 받는다 — 지난 반의 강사 이름 등이 비면 안 된다.
   - ⚠️ status 가 아예 없는 옛 문서는 status 조건 조회에서 통째로 빠진다(Firestore 동작).
     그래서 접속할 때 개수만 세어(1,000건당 1건 비용) 판정할 수 없는 문서가 하나라도 있으면
     예전처럼 전부 받는다. 판정할 수 없으면 거르지 않는다. 회원은 학생만 따진다(아래). */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { collection, onSnapshot, doc, query, where, getCountFromServer, documentId } from 'firebase/firestore';
import { db } from '../firebase';
import { APP_ID } from '../constants';

const DataContext = createContext();
const BASE = `artifacts/${APP_ID}/public/data`;

const STAFF_ROLES = ['admin', 'admin_assistant', 'lecturer', 'ta'];
const NON_STUDENT_ROLES = [...STAFF_ROLES, 'parent'];
/* 퇴원이 아닌 학생 상태. 여기 없는 값(또는 빈 값)을 가진 학생이 있으면 거르지 않는다. */
const KEPT_STUDENT_STATUSES = ['attending', 'resting', 'pending', 'active'];

const toList = (snapshot) => snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
// 예전 전체 구독과 같은 순서(문서 ID 순)를 유지한다. status 조건 조회는 status 순으로 온다.
const byId = (a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/* 회원: 교직원·학부모는 상태와 상관없이 늘 받고, 학생만 퇴원을 거른다.
   그래서 '모든 회원이 (교직원·학부모) 또는 (아는 상태의 학생) 또는 (퇴원 학생) 중 하나인가' 를 센다.
   교직원 계정에는 status 가 빈 옛 문서가 많아서(11-03 실측 16건) 전체 status 로 따지면 거르기가 영영 켜지지 않는다. */
const usersSplitIsComplete = async (col) => {
    try {
        const [all, nonStudents, kept, dropped] = await Promise.all([
            getCountFromServer(col),
            getCountFromServer(query(col, where('role', 'in', NON_STUDENT_ROLES))),
            getCountFromServer(query(col, where('role', '==', 'student'), where('status', 'in', KEPT_STUDENT_STATUSES))),
            getCountFromServer(query(col, where('role', '==', 'student'), where('status', '==', 'dropped')))
        ]);
        const missing = all.data().count - nonStudents.data().count - kept.data().count - dropped.data().count;
        if (missing > 0) {
            console.warn(`[DataContext] users: 역할이나 학생 상태를 판정할 수 없는 문서 ${missing}건 — 퇴원생을 거르지 않고 전부 받습니다.`);
        }
        return { ok: missing === 0, droppedStudents: dropped.data().count };
    } catch (e) {
        console.warn('[DataContext] users: 상태 확인 실패 — 전부 받습니다.', e);
        return { ok: false, droppedStudents: null };
    }
};

/* 이 컬렉션의 모든 문서에 status 가 들어 있는가.
   전체 개수 = (퇴원 아님) + (퇴원) 이면 빠진 문서가 없다. 확인하지 못하면 false. */
const statusIsComplete = async (col, label) => {
    try {
        const [all, kept, dropped] = await Promise.all([
            getCountFromServer(col),
            getCountFromServer(query(col, where('status', '!=', 'dropped'))),
            getCountFromServer(query(col, where('status', '==', 'dropped')))
        ]);
        const missing = all.data().count - kept.data().count - dropped.data().count;
        if (missing > 0) {
            console.warn(`[DataContext] ${label}: status 가 비어 있는 문서 ${missing}건 — 퇴원 기록을 거르지 않고 전부 받습니다.`);
        }
        return missing === 0;
    } catch (e) {
        console.warn(`[DataContext] ${label}: status 확인 실패 — 전부 받습니다.`, e);
        return false;
    }
};

export const DataProvider = ({ children, currentUser }) => {
    const [users, setUsers] = useState([]);
    const [classes, setClasses] = useState([]);
    const [enrollments, setEnrollments] = useState([]);
    const [masterData, setMasterData] = useState({ classrooms: [], subjects: [], seasons: [] });
    /* 환경설정에서 켠 부서(대과목). 반 과목 드롭다운이 이 값을 씁니다.
       예전에는 master_data.subjects 라는 편집 UI 없는 배열에만 의존했습니다. */
    const [activeDepartments, setActiveDepartments] = useState([]);
    // 학원 달력: 공휴일 / 휴원일 / 학원 행사. 모든 화면이 같은 값을 보게 한다.
    const [academyCalendar, setAcademyCalendar] = useState([]);
    const [loadingData, setLoadingData] = useState(true);

    /* 퇴원 기록을 거르고 있는가 (컬렉션별). 거르지 않으면 이미 전부 받은 것이다. */
    const [droppedFiltered, setDroppedFiltered] = useState({ users: false, enrollments: false });
    const [droppedStudentCount, setDroppedStudentCount] = useState(null);
    const [includeDropped, setIncludeDropped] = useState(false);
    const [droppedUsers, setDroppedUsers] = useState([]);
    const [droppedUsersArrived, setDroppedUsersArrived] = useState(false);
    const [droppedEnrollments, setDroppedEnrollments] = useState([]);

    const role = currentUser?.role;
    const isStaff = STAFF_ROLES.includes(role);

    useEffect(() => {
        if (!currentUser) {
            setLoadingData(false);
            return;
        }

        if (!isStaff && role !== 'student' && role !== 'parent') {
            setLoadingData(false);
            return;
        }

        const unsubs = [];
        let cancelled = false;
        // 권한 문제로 구독이 끊겼을 때 조용히 죽지 않도록 항상 오류를 남깁니다.
        const onErr = (label) => (e) => console.error(`[DataContext] ${label} 구독 실패:`, e);

        const sortClasses = (list) => list.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'ko-KR'));

        // ── 공통: 학원 마스터 설정 (개인정보 없음) ──────────────
        // 반 목록은 교직원만 전체를 받는다. 학생·학부모는 아래에서 자기(자녀) 반만 받는다.
        if (isStaff) {
            unsubs.push(onSnapshot(collection(db, `${BASE}/classes`), (snapshot) => {
                setClasses(sortClasses(toList(snapshot)));
            }, onErr('classes')));
        }

        unsubs.push(onSnapshot(doc(db, `${BASE}/settings`, 'master_data'), (docSnap) => {
            if (docSnap.exists()) {
                setMasterData({
                    classrooms: docSnap.data().classrooms || [],
                    subjects: docSnap.data().subjects || [],
                    seasons: docSnap.data().seasons || []
                });
            }
        }, onErr('settings/master_data')));

        unsubs.push(onSnapshot(doc(db, `${BASE}/settings`, 'departments'), (docSnap) => {
            setActiveDepartments(docSnap.exists() ? (docSnap.data().active || []) : []);
        }, onErr('settings/departments')));

        /* 학원 달력 — 로그인한 모두가 읽는다.
           "이 날 학원이 운영하는가"를 클리닉·강의·상담이 모두 이 값으로 판단한다.
           연 100건 남짓이라 전체 구독해도 부담이 없고, 개인정보는 담기지 않는다. */
        unsubs.push(onSnapshot(collection(db, `${BASE}/academy_calendar`), (snapshot) => {
            setAcademyCalendar(toList(snapshot));
        }, onErr('academy_calendar')));

        if (isStaff) {
            // ── 교직원: 업무상 전체 데이터가 필요합니다 (퇴원 기록은 위 설명대로 따로) ──
            const usersCol = collection(db, `${BASE}/users`);
            const enrollCol = collection(db, `${BASE}/enrollments`);
            const listen = (q, label, onList, onError) => {
                if (cancelled) return;
                unsubs.push(onSnapshot(q, (s) => onList(toList(s)), onError || onErr(label)));
            };

            (async () => {
                const [usersCheck, enrollOk] = await Promise.all([
                    usersSplitIsComplete(usersCol),
                    statusIsComplete(enrollCol, 'enrollments')
                ]);
                if (cancelled) return;
                const usersOk = usersCheck.ok;
                setDroppedFiltered({ users: usersOk, enrollments: enrollOk });
                setDroppedStudentCount(usersCheck.droppedStudents);

                if (usersOk) {
                    // 교직원·학부모 전부 + 퇴원 아닌 학생
                    let nonStudents = [];
                    let students = [];
                    const merge = () => setUsers([...nonStudents, ...students].sort(byId));
                    listen(query(usersCol, where('role', 'in', NON_STUDENT_ROLES)), 'users(staff/parents)',
                        (l) => { nonStudents = l; merge(); });
                    listen(query(usersCol, where('role', '==', 'student'), where('status', 'in', KEPT_STUDENT_STATUSES)), 'users(students)',
                        (l) => { students = l; merge(); });
                } else {
                    listen(usersCol, 'users', (l) => setUsers(l));
                }

                listen(enrollOk ? query(enrollCol, where('status', '!=', 'dropped')) : enrollCol, 'enrollments',
                    (l) => setEnrollments(enrollOk ? l.sort(byId) : l));
            })();

        } else {
            // ── 학생/학부모: 꼭 필요한 것만 ───────────────────────────────
            const childIds = Array.isArray(currentUser.linkedChildrenIds) ? currentUser.linkedChildrenIds : [];
            const ownerIds = [...new Set([currentUser.id, ...childIds])].filter(Boolean);

            let selfAndChildren = [];
            let staffList = [];
            const mergeUsers = () => setUsers([...selfAndChildren, ...staffList]);

            // 교직원 명부: 이름 / 역할 / 과목만 담긴 별도 컬렉션 (서버가 자동 동기화)
            unsubs.push(onSnapshot(collection(db, `${BASE}/staff_directory`), (snapshot) => {
                staffList = toList(snapshot);
                mergeUsers();
            }, onErr('staff_directory')));

            // 본인 / 자녀 문서: 목록 조회가 아니라 문서 단위로 직접 구독합니다.
            ownerIds.forEach((id) => {
                unsubs.push(onSnapshot(doc(db, `${BASE}/users`, id), (snap) => {
                    if (!snap.exists()) return;
                    const u = { id: snap.id, ...snap.data() };
                    selfAndChildren = [...selfAndChildren.filter(x => x.id !== u.id), u];
                    mergeUsers();
                }, onErr(`users/${id}`)));
            });

            /* 반 목록: 본인(자녀)이 수강한 반만 받는다(11-03 원장 확인 — 다른 반 목록을 보여 주는
               학생·학부모 화면은 없어도 된다). 예전에는 학생 한 명이 로그인할 때마다
               지난 시즌까지 학원의 모든 반을 받았다. 수강이 바뀌면 받는 반도 바꾼다. */
            let classKey = null;
            let classUnsubs = [];
            const stopClasses = () => { classUnsubs.forEach(fn => { try { fn(); } catch (e) { /* 이미 해제됨 */ } }); classUnsubs = []; };
            unsubs.push(stopClasses);
            const watchClasses = (classIds) => {
                const ids = [...new Set(classIds.filter(Boolean).map(String))].sort();
                const key = ids.join(',');
                if (key === classKey) return;
                classKey = key;
                stopClasses();
                if (ids.length === 0) { setClasses([]); return; }
                const chunks = [];
                for (let i = 0; i < ids.length; i += 30) chunks.push(ids.slice(i, i + 30));   // 'in' 은 30개까지
                const buckets = chunks.map(() => []);
                classUnsubs = chunks.map((chunk, idx) => onSnapshot(
                    query(collection(db, `${BASE}/classes`), where(documentId(), 'in', chunk)),
                    (snapshot) => { buckets[idx] = toList(snapshot); setClasses(sortClasses(buckets.flat())); },
                    onErr('classes(mine)')
                ));
            };

            // 수강 이력: 본인(또는 자녀) 것만
            if (ownerIds.length > 0) {
                // Firestore 'in' 연산자는 최대 30개까지 지원합니다.
                const targets = ownerIds.slice(0, 30);
                unsubs.push(onSnapshot(
                    query(collection(db, `${BASE}/enrollments`), where('studentId', 'in', targets)),
                    (snapshot) => {
                        const list = toList(snapshot);
                        setEnrollments(list);
                        watchClasses(list.map(e => e.classId));
                    },
                    onErr('enrollments(mine)')
                ));
            } else {
                setClasses([]);
            }
        }

        setLoadingData(false);

        return () => {
            cancelled = true;
            unsubs.forEach(fn => { try { fn(); } catch (e) { /* 이미 해제됨 */ } });
        };
    }, [currentUser, isStaff, role]);

    /* 퇴원 기록 — showDropped() 를 부른 뒤에만, 거르고 있는 컬렉션만 받는다. */
    useEffect(() => {
        if (!isStaff || !includeDropped) return undefined;
        const unsubs = [];
        const onErr = (label) => (e) => console.error(`[DataContext] ${label} 구독 실패:`, e);
        if (droppedFiltered.users) {
            unsubs.push(onSnapshot(
                query(collection(db, `${BASE}/users`), where('status', '==', 'dropped'), where('role', '==', 'student')),
                (s) => { setDroppedUsers(toList(s)); setDroppedUsersArrived(true); }, onErr('users(dropped students)')));
        }
        if (droppedFiltered.enrollments) {
            unsubs.push(onSnapshot(
                query(collection(db, `${BASE}/enrollments`), where('status', '==', 'dropped')),
                (s) => setDroppedEnrollments(toList(s)), onErr('enrollments(dropped)')));
        }
        return () => unsubs.forEach(fn => { try { fn(); } catch (e) { /* 이미 해제됨 */ } });
    }, [isStaff, includeDropped, droppedFiltered]);

    const showDropped = useCallback(() => setIncludeDropped(true), []);

    const allUsers = useMemo(() => (
        includeDropped && droppedFiltered.users && droppedUsers.length > 0
            ? [...users, ...droppedUsers].sort(byId) : users
    ), [users, droppedUsers, includeDropped, droppedFiltered.users]);

    const allEnrollments = useMemo(() => (
        includeDropped && droppedFiltered.enrollments && droppedEnrollments.length > 0
            ? [...enrollments, ...droppedEnrollments].sort(byId) : enrollments
    ), [enrollments, droppedEnrollments, includeDropped, droppedFiltered.enrollments]);

    /* 퇴원생이 지금 목록에서 빠져 있는가. 회원 관리가 '불러오기' 안내를 띄울 때 쓴다. */
    const droppedHidden = isStaff && !includeDropped && droppedFiltered.users;
    /* 지금 users 에 퇴원생까지 다 들어 있는가. 거르지 않고 있으면 처음부터 다 들어 있다.
       '이 사람이 퇴원생인가' 를 판단해야 하는 화면(메시지 센터)이 이 값을 보고 기다린다. */
    const droppedLoaded = !droppedFiltered.users || (includeDropped && droppedUsersArrived);

    /* 🐛 [버그 수정] currentUser를 Context에 함께 제공합니다.
       ConsultationManager / RecruitmentManager / AcademicCalendarManager는
       useData()에서 currentUser를 꺼내 쓰고 있었지만 Provider가 넘겨주지 않아
       항상 undefined였습니다. (예: 상담 등록 시 담당자가 비어 있던 원인) */
    return (
        <DataContext.Provider value={{
            currentUser, users: allUsers, classes, enrollments: allEnrollments, masterData, activeDepartments,
            academyCalendar, loadingData, showDropped, droppedHidden, droppedLoaded, droppedStudentCount
        }}>
            {children}
        </DataContext.Provider>
    );
};

export const useData = () => useContext(DataContext);
